import { GripVertical } from 'lucide-react';
import { useState, type DragEvent, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { EmptyState, StatusDot, type StatusDotProps } from '@/shared/ui';
import { CourtIcon } from './SportIcon';

/** Status kortu — odpowiada legendzie „Gotowy do gry / Za 15-30 min / Wolny”. */
export type CourtStatus = 'busy' | 'soon' | 'break' | 'free';

const courtStatusDot: Record<CourtStatus, NonNullable<StatusDotProps['status']>> = {
  busy: 'finished',
  soon: 'walkover',
  break: 'idle',
  free: 'idle',
};

export interface CourtStatusEntry {
  id: string;
  name: string;
  status: CourtStatus;
  /** Opis stanu, np. „Trwa mecz”, „Za 15 min”, „Wolny”. */
  statusLabel: string;
}

export interface CourtStatusListProps {
  courts: readonly CourtStatusEntry[];
  className?: string;
}

/** Lista kortow ze statusem — panel „STATUS KORTÓW” z centrum turnieju. */
export function CourtStatusList({ courts, className }: CourtStatusListProps) {
  return (
    <ul className={cn('flex flex-col', className)}>
      {courts.map((court) => (
        <li
          key={court.id}
          className="border-border flex items-center gap-3 border-b py-2.5 last:border-b-0"
        >
          <span className="text-fg-strong w-16 shrink-0 text-[0.8125rem] font-semibold">
            {court.name}
          </span>
          <span className="text-fg-muted min-w-0 flex-1 truncate text-[0.8125rem]">
            {court.statusLabel}
          </span>
          <StatusDot status={courtStatusDot[court.status]} label={court.statusLabel} />
        </li>
      ))}
    </ul>
  );
}

export interface CourtCardProps {
  name: string;
  /** Rodzaj nawierzchni, np. „Padel”, „Sztuczna trawa”. */
  surface?: string;
  status: CourtStatus;
  statusLabel: string;
  /** Blok z meczem rozgrywanym teraz. */
  current?: ReactNode;
  /** Blok „Następnie”. */
  next?: ReactNode;
  /** Akcja w prawym gornym rogu (np. menu ⋮). */
  action?: ReactNode;
  className?: string;
}

const courtStatusText: Record<CourtStatus, string> = {
  busy: 'text-success-600',
  soon: 'text-warning-600',
  break: 'text-fg-subtle',
  free: 'text-fg-subtle',
};

/** Karta kortu z aktualnym i nastepnym meczem — z ekranu przydziału meczów. */
export function CourtCard({
  name,
  surface,
  status,
  statusLabel,
  current,
  next,
  action,
  className,
}: CourtCardProps) {
  return (
    <div className={cn('border-border bg-surface flex flex-col rounded-xl border p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-fg-strong text-base font-bold tracking-[-0.02em]">{name}</p>
          {surface && <p className="text-fg-subtle text-xs">{surface}</p>}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className={cn('flex items-center gap-1.5 text-xs font-medium', courtStatusText[status])}>
            <StatusDot status={courtStatusDot[status]} />
            {statusLabel}
          </span>
          {action}
        </div>
      </div>

      {current && <div className="mt-3">{current}</div>}

      {next && (
        <div className="mt-3">
          <p className="text-fg-muted mb-1.5 text-xs">Następnie</p>
          {next}
        </div>
      )}
    </div>
  );
}

export interface DropZoneProps {
  /** Tekst glowny, np. „Przeciągnij mecz tutaj”. */
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Wywolywane po upuszczeniu elementu. Otrzymuje dane z `dataTransfer`. */
  onDropItem?: (payload: string) => void;
  /** Wywolywane po kliknieciu — alternatywa dla przeciagania. */
  onClick?: () => void;
  className?: string;
}

/**
 * Obszar upuszczania — „Przeciągnij mecz tutaj lub kliknij, aby wybrać mecz”.
 *
 * Zawsze udostepnia rowniez klikniecie: przeciaganie jest niedostepne dla osob
 * korzystajacych z klawiatury, wiec sama ta droga bylaby wykluczajaca.
 */
export function DropZone({
  title,
  description,
  icon,
  onDropItem,
  onClick,
  className,
}: DropZoneProps) {
  const [isOver, setIsOver] = useState(false);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);
    onDropItem?.(event.dataTransfer.getData('text/plain'));
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={handleDrop}
      className={cn('transition-colors', isOver && 'scale-[1.01]', className)}
    >
      {onClick ? (
        <button type="button" onClick={onClick} className="w-full text-left">
          <EmptyState
            dashed
            size="sm"
            icon={icon ?? <CourtIcon />}
            title={title}
            {...(description ? { description } : {})}
            className={cn(isOver && 'border-brand bg-brand-subtle')}
          />
        </button>
      ) : (
        <EmptyState
          dashed
          size="sm"
          icon={icon ?? <CourtIcon />}
          title={title}
          {...(description ? { description } : {})}
          className={cn(isOver && 'border-brand bg-brand-subtle')}
        />
      )}
    </div>
  );
}

export interface DraggableCardProps {
  /** Wartosc przekazywana w `dataTransfer` przy przeciaganiu. */
  payload: string;
  children: ReactNode;
  className?: string;
}

/** Karta, ktora mozna przeciagnac na kort — z uchwytem po lewej stronie. */
export function DraggableCard({ payload, children, className }: DraggableCardProps) {
  return (
    <div
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', payload);
        event.dataTransfer.effectAllowed = 'move';
      }}
      className={cn(
        'border-border bg-surface flex items-center gap-2 rounded-xl border p-2.5',
        'cursor-grab active:cursor-grabbing',
        className,
      )}
    >
      <GripVertical aria-hidden="true" className="text-fg-subtle size-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
